import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  BarChart3, TrendingUp, DollarSign, Building,
  Plus, Pencil, Trash2, LogOut, Menu, X,
  Box, Megaphone, Tag, HelpCircle, Upload, Star, Bot, UserCog
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Import tab components
import DashboardTab from '../components/admin/DashboardTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import OrdersTab from '../components/admin/OrdersTab';
import CustomersTab from '../components/admin/CustomersTab';
import CompaniesTab from '../components/admin/CompaniesTab';
import InventoryTab from '../components/admin/InventoryTab';
import CostingTab from '../components/admin/CostingTab';
import PnLTab from '../components/admin/PnLTab';
import CouponsTab from '../components/admin/CouponsTab';
import FAQsTab from '../components/admin/FAQsTab';
import CampaignsTab from '../components/admin/CampaignsTab';
import CatalogImportTab from '../components/admin/CatalogImportTab';
import AIAgentsTab from '../components/admin/AIAgentsTab';
import UsersTab from '../components/admin/UsersTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [salesAnalytics, setSalesAnalytics] = useState(null);
  const [salesPeriod, setSalesPeriod] = useState('month');
  const [companyAnalytics, setCompanyAnalytics] = useState([]);
  const [hourlyAnalytics, setHourlyAnalytics] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [projections, setProjections] = useState(null);
  const [inventory, setInventory] = useState({ items: [], summary: {} });
  const [costing, setCosting] = useState({ products: [], total_potential_profit: 0 });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [detailedUsers, setDetailedUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [pnlData, setPnlData] = useState(null);
  const [pnlPeriod, setPnlPeriod] = useState('monthly');
  const [pnlDate, setPnlDate] = useState(new Date().toISOString().slice(0, 10));
  const [projectedPnl, setProjectedPnl] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [faqs, setFaqs] = useState([]);
  
  // Product form states
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', cost: '', category_id: '',
    image_url: '', weight: '', temperature: '', stock: 100, min_stock: 10,
    discount_percent: 0, discount_price: '', promotion_active: false,
    promotion_label: '', promotion_start: '', promotion_end: '',
    calories: '', protein_g: '', carbs_g: '', fat_g: '', fiber_g: '',
    sku: '', supplier: '', tags: [], featured: false
  });
  const [productSearch, setProductSearch] = useState('');
  const [productFilter, setProductFilter] = useState({ category: 'all', status: 'all' });

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'analytics', label: 'Analíticas', icon: BarChart3 },
    { id: 'ai-agents', label: 'AI Agents', icon: Bot },
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart },
    { id: 'products', label: 'Productos', icon: Package },
    { id: 'import', label: 'Importar', icon: Upload },
    { id: 'inventory', label: 'Inventario', icon: Box },
    { id: 'costing', label: 'Costeo', icon: DollarSign },
    { id: 'pnl', label: 'P&L', icon: TrendingUp },
    { id: 'users', label: 'Usuarios', icon: UserCog },
    { id: 'customers', label: 'Clientes', icon: Users },
    { id: 'companies', label: 'Empresas', icon: Building },
    { id: 'campaigns', label: 'Campañas', icon: Megaphone },
    { id: 'coupons', label: 'Cupones', icon: Tag },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle },
  ];

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'master_admin'].includes(user.role))) {
      navigate('/');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ['admin', 'master_admin'].includes(user.role)) {
      fetchAllData();
    }
  }, [user]);

  useEffect(() => {
    if (user && ['admin', 'master_admin'].includes(user.role)) {
      fetchSalesAnalytics();
    }
  }, [salesPeriod, user]);

  useEffect(() => {
    if (user && ['admin', 'master_admin'].includes(user.role)) {
      fetchPnL();
    }
  }, [pnlPeriod, pnlDate, user]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const endpoints = [
        { url: '/admin/stats', setter: setStats },
        { url: '/admin/analytics/sales-by-company', setter: setCompanyAnalytics },
        { url: '/admin/analytics/top-customers?limit=10', setter: setTopCustomers },
        { url: '/admin/analytics/top-products?limit=10', setter: setTopProducts },
        { url: '/admin/projections', setter: setProjections },
        { url: '/admin/inventory', setter: setInventory },
        { url: '/admin/costing', setter: setCosting },
        { url: '/admin/products/all', setter: setProducts },
        { url: '/categories', setter: setCategories },
        { url: '/admin/orders', setter: setOrders },
        { url: '/admin/users/detailed', setter: setDetailedUsers },
        { url: '/admin/companies', setter: setCompanies },
        { url: '/admin/campaigns', setter: setCampaigns },
        { url: '/admin/coupons', setter: setCoupons },
        { url: '/admin/faqs', setter: setFaqs },
      ];

      await Promise.all(endpoints.map(async ({ url, setter }) => {
        try {
          const res = await fetch(`${API}${url}`, { credentials: 'include' });
          if (res.ok) setter(await res.json());
        } catch (e) {
          console.error(`Error fetching ${url}:`, e);
        }
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesAnalytics = async () => {
    try {
      const res = await fetch(`${API}/admin/analytics/sales?period=${salesPeriod}`, { credentials: 'include' });
      if (res.ok) setSalesAnalytics(await res.json());
    } catch (error) {
      console.error('Error fetching sales analytics:', error);
    }
  };

  const fetchPnL = async () => {
    try {
      const [pnlRes, projRes] = await Promise.all([
        fetch(`${API}/operations/pnl?period=${pnlPeriod}&date=${pnlDate}`, { credentials: 'include' }),
        fetch(`${API}/operations/pnl/projected`, { credentials: 'include' })
      ]);
      if (pnlRes.ok) setPnlData(await pnlRes.json());
      if (projRes.ok) setProjectedPnl(await projRes.json());
    } catch (error) {
      console.error('Error fetching P&L:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success('Estado actualizado');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Product handlers
  const handleSaveProduct = async () => {
    try {
      const url = editingProduct 
        ? `${API}/admin/products/${editingProduct.product_id}`
        : `${API}/admin/products`;
      
      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...productForm,
          price: parseFloat(productForm.price) || 0,
          cost: parseFloat(productForm.cost) || 0,
          stock: parseInt(productForm.stock) || 0,
          min_stock: parseInt(productForm.min_stock) || 0
        })
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Producto actualizado' : 'Producto creado');
        setProductDialogOpen(false);
        setEditingProduct(null);
        resetProductForm();
        fetchAllData();
      }
    } catch (error) {
      toast.error('Error al guardar el producto');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '', description: '', price: '', cost: '', category_id: '',
      image_url: '', weight: '', temperature: '', stock: 100, min_stock: 10,
      discount_percent: 0, discount_price: '', promotion_active: false,
      promotion_label: '', promotion_start: '', promotion_end: '',
      calories: '', protein_g: '', carbs_g: '', fat_g: '', fiber_g: '',
      sku: '', supplier: '', tags: [], featured: false
    });
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      cost: product.cost?.toString() || '0',
      category_id: product.category_id || '',
      image_url: product.image_url || '',
      weight: product.weight || '',
      temperature: product.temperature || '',
      stock: product.stock || 100,
      min_stock: product.min_stock || 10,
      discount_percent: product.discount_percent || 0,
      discount_price: product.discount_price?.toString() || '',
      promotion_active: product.promotion_active || false,
      promotion_label: product.promotion_label || '',
      promotion_start: product.promotion_start || '',
      promotion_end: product.promotion_end || '',
      calories: product.calories?.toString() || '',
      protein_g: product.protein_g?.toString() || '',
      carbs_g: product.carbs_g?.toString() || '',
      fat_g: product.fat_g?.toString() || '',
      fiber_g: product.fiber_g?.toString() || '',
      sku: product.sku || '',
      supplier: product.supplier || '',
      tags: product.tags || [],
      featured: product.featured || false
    });
    setProductDialogOpen(true);
  };

  const handleToggleActive = async (productId) => {
    try {
      const response = await fetch(`${API}/admin/products/${productId}/toggle-active`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Estado actualizado');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Producto eliminado');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const filteredProducts = products.filter(p => {
    if (productSearch && !p.name?.toLowerCase().includes(productSearch.toLowerCase())) return false;
    if (productFilter.category !== 'all' && p.category_id !== productFilter.category) return false;
    if (productFilter.status === 'active' && !p.active) return false;
    if (productFilter.status === 'inactive' && p.active) return false;
    return true;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-stone-200 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-stone-200 flex items-center justify-between">
            <img src="/logo.png" alt="Lunch & Roll" className="h-10" />
            <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id
                    ? 'bg-emerald-50 text-emerald-700 font-medium'
                    : 'text-stone-600 hover:bg-stone-50'
                }`}
                data-testid={`admin-nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>
          
          <div className="p-4 border-t border-stone-200">
            <div className="flex items-center gap-3 mb-4">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900 truncate">{user?.name}</p>
                <p className="text-xs text-stone-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-stone-200 p-4 flex items-center justify-between sticky top-0 z-40">
          <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </Button>
          <h1 className="font-bold text-emerald-700">Admin</h1>
          <div className="w-8" />
        </header>

        <div className="p-4 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <DashboardTab
              stats={stats}
              projections={projections}
              companyAnalytics={companyAnalytics}
              topProducts={topProducts}
              inventory={inventory}
              onRefresh={fetchAllData}
            />
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <AnalyticsTab
              salesAnalytics={salesAnalytics}
              salesPeriod={salesPeriod}
              setSalesPeriod={setSalesPeriod}
              companyAnalytics={companyAnalytics}
              topCustomers={topCustomers}
              topProducts={topProducts}
              hourlyAnalytics={hourlyAnalytics}
            />
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <OrdersTab
              orders={orders}
              companies={companies}
              onUpdateStatus={handleUpdateOrderStatus}
            />
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h1 className="text-2xl font-bold text-stone-900">Gestión de Productos</h1>
                <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-emerald-700 hover:bg-emerald-800 rounded-full"
                      onClick={() => { setEditingProduct(null); resetProductForm(); }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Producto
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="text-sm font-medium">Nombre *</label>
                          <Input
                            value={productForm.name}
                            onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-sm font-medium">Descripción</label>
                          <textarea
                            value={productForm.description}
                            onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                            className="mt-1 w-full rounded-md border border-stone-200 p-2 text-sm min-h-[80px]"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Precio *</label>
                          <Input
                            type="number"
                            value={productForm.price}
                            onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Costo</label>
                          <Input
                            type="number"
                            value={productForm.cost}
                            onChange={(e) => setProductForm({...productForm, cost: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Categoría *</label>
                          <Select
                            value={productForm.category_id}
                            onValueChange={(v) => setProductForm({...productForm, category_id: v})}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map(cat => (
                                <SelectItem key={cat.category_id} value={cat.category_id}>
                                  {cat.name_es}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Stock</label>
                          <Input
                            type="number"
                            value={productForm.stock}
                            onChange={(e) => setProductForm({...productForm, stock: parseInt(e.target.value)})}
                            className="mt-1"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="text-sm font-medium">URL Imagen</label>
                          <Input
                            value={productForm.image_url}
                            onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                            className="mt-1"
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                      <Button onClick={handleSaveProduct} className="w-full bg-emerald-700 hover:bg-emerald-800">
                        {editingProduct ? 'Actualizar' : 'Crear'} Producto
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Filters */}
              <div className="flex gap-2 flex-wrap">
                <Input
                  placeholder="Buscar..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-48"
                />
                <Select value={productFilter.category} onValueChange={(v) => setProductFilter({...productFilter, category: v})}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name_es}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={productFilter.status} onValueChange={(v) => setProductFilter({...productFilter, status: v})}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="inactive">Inactivos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Products Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map(product => (
                  <div key={product.product_id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-video bg-stone-100 relative">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-stone-300" />
                        </div>
                      )}
                      {product.featured && (
                        <div className="absolute top-2 left-2">
                          <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                        </div>
                      )}
                      <Badge className={`absolute top-2 right-2 ${product.active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                        {product.active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-stone-900 truncate">{product.name}</h3>
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-lg font-bold text-emerald-700">${product.price}</p>
                        <p className="text-sm text-stone-500">Costo: ${product.cost || 0}</p>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditProduct(product)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleToggleActive(product.product_id)}>
                          {product.active ? 'Desact.' : 'Activar'}
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteProduct(product.product_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Import Tab */}
          {activeTab === 'import' && (
            <CatalogImportTab categories={categories} onImportComplete={fetchAllData} />
          )}

          {/* Inventory Tab */}
          {activeTab === 'inventory' && (
            <InventoryTab inventory={inventory} />
          )}

          {/* Costing Tab */}
          {activeTab === 'costing' && (
            <CostingTab costing={costing} />
          )}

          {/* P&L Tab */}
          {activeTab === 'pnl' && (
            <PnLTab
              pnlData={pnlData}
              pnlPeriod={pnlPeriod}
              setPnlPeriod={setPnlPeriod}
              pnlDate={pnlDate}
              setPnlDate={setPnlDate}
              projectedPnl={projectedPnl}
            />
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <CustomersTab detailedUsers={detailedUsers} />
          )}

          {/* Companies Tab */}
          {activeTab === 'companies' && (
            <CompaniesTab companies={companies} />
          )}

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <CampaignsTab
              campaigns={campaigns}
              companies={companies}
              detailedUsers={detailedUsers}
              onRefresh={fetchAllData}
            />
          )}

          {/* Coupons Tab */}
          {activeTab === 'coupons' && (
            <CouponsTab coupons={coupons} onRefresh={fetchAllData} />
          )}

          {/* FAQs Tab */}
          {activeTab === 'faqs' && (
            <FAQsTab faqs={faqs} onRefresh={fetchAllData} />
          )}

          {/* AI Agents Tab */}
          {activeTab === 'ai-agents' && (
            <AIAgentsTab />
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <UsersTab />
          )}
        </div>
      </main>
    </div>
  );
}
